import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  CircleArrowLeft,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useDeleteSessionByIdMutation } from '~entities/session/api/delete-session-by-id';
import {
  getClassEventCountSessionByIdOptions,
  useGetClassEventCountSessionById,
} from '~entities/session/api/get-class-event-count-session-by-id';
import { useGetAllSessions } from '~entities/session/api/get-sessions';
import {
  ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY,
  useDeleteSessionRollover,
  useSessionRolloverJobs,
} from '~entities/session/api/session-rollover';
import type { Session } from '~entities/session/modal/session';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table/header';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import { Label } from '~shared/shadcn/ui/label';
import { Switch } from '~shared/shadcn/ui/switch';
import { formatDateIgnoreTimezone } from '~shared/utils/date-time';

import { ArchiveSessionButton } from './archive-session-button';
import { CopySessionButton } from './copy-session-button';

const columnHelper = createColumnHelper<Session>();

const DeleteSessionButton = ({ id }: { id: string }) => {
  const [showDialog, setShowDialog] = useState(false);
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteSessionByIdMutation();
  const { data: countData } = useGetClassEventCountSessionById(id);

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ id });
      toast.success('Session deleted successfully');
    } catch (error) {
      console.error('🚀 ~ handleDelete ~ error:', error);
      toast.error('Failed to delete session');
    }
  };

  const handleDeleteClick = async () => {
    try {
      const freshCount = await queryClient.fetchQuery({
        ...getClassEventCountSessionByIdOptions(id),
        staleTime: 0,
      });
      if (freshCount.classes > 0 || freshCount.events > 0) {
        setShowDialog(true);
        return;
      }
      await handleDelete();
    } catch (error) {
      console.error('🚀 ~ handleDeleteClick ~ error:', error);
      toast.error('Failed to check whether this session can be deleted');
    }
  };

  return (
    <>
      <Button
        variant={'destructive'}
        className='size-6 p-0'
        onClick={handleDeleteClick}
      >
        {deleteMutation.isPending ? (
          <Loader2 className='size-6 animate-spin' />
        ) : (
          <Trash2 className='size-6' />
        )}
      </Button>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Delete Session</AlertDialogTitle>
            <AlertDialogDescription>
              This session has{' '}
              <span className='font-bold'>
                {[
                  countData?.classes
                    ? `${countData.classes} ${countData.classes > 1 ? 'classes' : 'class'}`
                    : null,
                  countData?.events
                    ? `${countData.events} ${countData.events > 1 ? 'events' : 'event'}`
                    : null,
                ]
                  .filter((item): item is string => item !== null)
                  .join(' and ')}{' '}
              </span>
              associated with it. Please remove the session from{' '}
              {countData?.classes && countData.events ? 'them' : 'it'} before
              deleting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>OK</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const columns = [
  columnHelper.accessor('name', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Name'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      return (
        <div className='px-4 capitalize'>
          {info.getValue()}
          <div className='mt-1 flex gap-2'>
            {info.row.original.isEventDefault && (
              <Badge className=''>Event Default</Badge>
            )}
            {info.row.original.isClassDefault && (
              <Badge className=''>Class Default</Badge>
            )}
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('startDate', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Start Date'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      return (
        <div className='px-4'>
          <span>{value ? formatDateIgnoreTimezone(value) : '-'}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('endDate', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='End Date'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      return (
        <div className='px-4'>
          <span>{value ? formatDateIgnoreTimezone(value) : '-'}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('registrationFeeAmount', {
    id: 'Registration Fee amount',
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Registration Fee'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const session = info.row.original;
      const hasPerStudentFees =
        session.registrationFeePerStudent &&
        Object.keys(session.registrationFeePerStudent).length > 0;
      const hasPerFamilyFee =
        session.registrationFeeAmount && session.registrationFeeAmount > 0;

      if (hasPerStudentFees && hasPerFamilyFee) {
        // Both per-student and per-family fees exist
        const perStudentEntries = Object.entries(
          session.registrationFeePerStudent ?? {},
        ).filter(([_, fee]) => fee > 0);

        const isFlatFee =
          perStudentEntries.length === 1 && perStudentEntries[0]?.[0] === '1';

        const studentFees = perStudentEntries.map(([student, fee]) =>
          isFlatFee ? `Flat Fee: $${fee}` : `Student ${student}: $${fee}`,
        );

        const displayText =
          studentFees.length > 2
            ? `${studentFees.slice(0, 2).join(', ')}, ...`
            : studentFees.join(', ');

        return (
          <div className='px-4'>
            <div className='rounded-lg border border-gray-100 bg-white p-3 shadow-sm'>
              {session.isRegistrationFee && (
                <div className='text-sm font-medium text-gray-700'>
                  Per Family: ${session.registrationFeeAmount}
                </div>
              )}
              <div className='mt-1 text-sm text-gray-600'>
                Per Student: {displayText}
              </div>
            </div>
          </div>
        );
      } else if (hasPerStudentFees) {
        // Only per-student fees exist
        const perStudentEntries = Object.entries(
          session.registrationFeePerStudent ?? {},
        ).filter(([_, fee]) => fee > 0);

        const isFlatFee =
          perStudentEntries.length === 1 && perStudentEntries[0]?.[0] === '1';

        if (isFlatFee) {
          return (
            <div className='px-4'>
              <div className='rounded-lg border border-gray-100 bg-white p-3 shadow-sm'>
                <div className='text-sm font-medium text-gray-700'>
                  Flat Fee: ${perStudentEntries[0]?.[1]}
                </div>
              </div>
            </div>
          );
        } else {
          const studentFees = perStudentEntries.map(
            ([student, fee]) => `Student ${student}: $${fee}`,
          );
          const displayText =
            studentFees.length > 2
              ? `${studentFees.slice(0, 2).join(', ')}, ...`
              : studentFees.join(', ');

          return (
            <div className='px-4'>
              <div className='rounded-lg border border-gray-100 bg-white p-3 shadow-sm'>
                <div className='text-sm font-medium text-gray-700'>
                  Per Student: {displayText}
                </div>
              </div>
            </div>
          );
        }
      } else if (hasPerFamilyFee && session.isRegistrationFee) {
        // Only per-family fee exists
        return (
          <div className='px-4'>
            <div className='rounded-lg border border-gray-100 bg-white p-3 shadow-sm'>
              <div className='text-sm font-medium text-gray-700'>
                Per Family: ${session.registrationFeeAmount}
              </div>
            </div>
          </div>
        );
      } else {
        // No fees
        return (
          <div className='px-4'>
            <span className='text-gray-400'>-</span>
          </div>
        );
      }
    },
  }),
  columnHelper.accessor('billingDate', {
    id: 'Billing Date',
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Billing Date'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      return (
        <div className='px-4'>
          <span>{value ? formatDateIgnoreTimezone(value) : '-'}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor('visibleInParentPortal', {
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Visible in Parent Portal'
        className='pl-4 text-black'
        disableSorting
      />
    ),
    cell: (info) => {
      const value = info.getValue();
      return (
        <div className='px-4'>
          <Badge variant={value ? 'secondary' : 'destructive'}>
            {value ? 'Yes' : 'No'}
          </Badge>
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'Actions',
    header: (info) => (
      <DataTableColumnHeader
        column={info.column}
        title='Actions'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => {
      const session = info.row.original;
      return (
        <div className='flex items-center gap-2 px-4'>
          <Button className='size-6 p-0 text-secondary' asChild>
            <Link
              to={`/sessions/$sessionId/edit`}
              params={{ sessionId: session._id }}
            >
              <Pencil className='size-6' />
            </Link>
          </Button>
          <CopySessionButton session={session} />
          <Button
            variant='outline'
            className='size-6 p-0 text-secondary'
            title='Roll over session'
            asChild
          >
            <Link
              to='/sessions/$sessionId/rollover'
              params={{ sessionId: session._id }}
            >
              <RefreshCw className='size-6' />
            </Link>
          </Button>
          <ArchiveSessionButton
            id={session._id}
            isArchived={session.isArchive}
          />
          <DeleteSessionButton id={session._id} />
        </div>
      );
    },
  }),
];

export function SessionsListPage() {
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isFetching } = useGetAllSessions({
    name: searchQuery,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    isArchive: showArchived,
  });
  const { data: rolloverJobs } = useSessionRolloverJobs();
  const deleteRolloverMutation = useDeleteSessionRollover();
  const activeRollover = rolloverJobs?.find(
    (job) => job.status === 'queued' || job.status === 'processing',
  );
  const latestCleanupCandidate = rolloverJobs?.find((job) => job.canDelete);

  const tableData = data?.sessions ?? [];

  return (
    <div>
      <div className='mb-8 flex items-center gap-2'>
        <Button
          variant={'ghost'}
          className='flex items-center p-2 [&_svg]:size-6'
          onClick={() => {
            void navigate({
              to: '/dashboard',
            });
          }}
        >
          <CircleArrowLeft className='size-6' />
        </Button>
        <h2 className='text-2xl font-semibold'>Sessions</h2>
      </div>

      {activeRollover && (
        <div className='mb-4 flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 p-4'>
          <div>
            <p className='font-medium'>
              We are moving classes and students right now
            </p>
            <p className='text-sm text-muted-foreground'>
              {activeRollover.currentItem ?? activeRollover.phase} ·{' '}
              {activeRollover.progress}% done
            </p>
          </div>
          <Button asChild>
            <Link
              to='/sessions/rollover'
              onClick={() => {
                sessionStorage.setItem(
                  ACTIVE_SESSION_ROLLOVER_JOB_STORAGE_KEY,
                  activeRollover.jobId,
                );
              }}
            >
              View progress
            </Link>
          </Button>
        </div>
      )}

      {!activeRollover && latestCleanupCandidate && (
        <div className='mb-4 flex items-center justify-between rounded-lg border p-4'>
          <div>
            <p className='font-medium'>Your last session rollover</p>
            <p className='text-sm text-muted-foreground'>
              {latestCleanupCandidate.status.split('_').join(' ')} · If you
              picked the wrong session, you can undo everything it created.
            </p>
          </div>
          <Button
            variant='destructive'
            disabled={deleteRolloverMutation.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'This removes the classes, enrollments, and invoices this rollover created in the new session, and puts the old enrollments back. Continue?',
                )
              ) {
                void deleteRolloverMutation
                  .mutateAsync(latestCleanupCandidate.jobId)
                  .then((result) => {
                    result.warnings.forEach((warning) =>
                      toast.warning(warning),
                    );
                    toast.success(
                      'Everything this rollover created has been removed.',
                    );
                  });
              }
            }}
          >
            {deleteRolloverMutation.isPending ? (
              <Loader2 className='animate-spin' />
            ) : (
              <Trash2 />
            )}
            {deleteRolloverMutation.isPending
              ? 'Undoing rollover...'
              : 'Undo this rollover'}
          </Button>
        </div>
      )}

      <EnrollioTable
        ariaLabel='Sessions'
        totalLabel='Sessions'
        filterHeader={
          <EnrollioTableSearch
            placeholder='Search Sessions'
            value={searchQuery}
            onChange={setSearchQuery}
          />
        }
        columns={columns}
        data={tableData}
        rowCount={data?.pagination.total ?? 0}
        isUpdating={isFetching}
        sorting={sorting}
        onSortingChange={setSorting}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        enableRowSelection={true}
        manualPagination
        pagination={pagination}
        onPaginationChange={setPagination}
        toolbar={(table) => (
          <>
            <div className='flex items-center space-x-2'>
              <Switch
                id='show-archived'
                checked={showArchived}
                onCheckedChange={(checked) => {
                  setShowArchived(checked);
                }}
              />
              <Label htmlFor='show-archived'>Show Archived Sessions</Label>
            </div>
            <Button
              className='bg-custom-secondary-1 text-white hover:bg-custom-secondary-1'
              asChild
            >
              <Link to='/sessions/new'>
                <Plus />
                Add Session
              </Link>
            </Button>
            <EnrollioTableColumnToggle table={table} />
          </>
        )}
      />
    </div>
  );
}
